import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from "../apiClient";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, eachDayOfInterval } from 'date-fns';
import Select from 'react-select';
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function PayrollReportPage() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [committedStartDate, setCommittedStartDate] = useState(null);
  const [committedEndDate, setCommittedEndDate] = useState(null);
  const [reportDuration, setReportDuration] = useState(null);
  const isMountedRef = useRef(true);
  const [unlicensedDrivers, setUnlicensedDrivers] = useState([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Load drivers on component mount
  useEffect(() => {
    const loadDrivers = async () => {
      try {
        const res = await api.get('/api/drivers');
        const driverOptions = res.data.map(v => ({
          value: v.id,
          label: v.name
        }));
        setDrivers(driverOptions);
      } catch (err) {
        console.error('Failed to load drivers:', err);
        setError('Failed to load drivers. Please refresh and try again.');
      }
    };

    loadDrivers();
  }, []);

  // Listen for token refresh events
  useEffect(() => {
    const handleTokenRefresh = async () => {
      if (user && user.refreshToken) {
        await user.refreshToken();
      }
    };

    window.addEventListener('tokenRefreshNeeded', handleTokenRefresh);
    return () => window.removeEventListener('tokenRefreshNeeded', handleTokenRefresh);
  }, [user]);

  const handleDateChange = useCallback((update) => {
    setDateRange(update);
  }, []);

  const handleCancel = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
    setLoading(false);
  }, [abortController]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDrivers.length === 0) return setError("Select at least one driver");
    if (!startDate || !endDate) return setError("Select a date range");
    if (startDate > endDate) return setError("Start date must be before end date");
    if (Math.ceil((endDate - startDate) / (1000*60*60*24)) > 90)
      return setError("Date range cannot exceed 90 days");

    setCommittedStartDate(startDate);
    setCommittedEndDate(endDate);

    setReportData(null);
    setError("");
    setLoading(true);
    setReportDuration(null);

    const controller = new AbortController();
    setAbortController(controller);

    const formattedStart = format(startDate, "yyyy-MM-dd");
    const formattedEnd = format(endDate, "yyyy-MM-dd");

    const startTime = performance.now();

    try {
      const driverIds = selectedDrivers.map(d => d.value);
      const res = await api.post("/api/payrollreport", {
        drivers: driverIds,
        start_date: formattedStart,
        end_date: formattedEnd
      }, { signal: controller.signal });

      if (!isMountedRef.current) return;

      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);
      setReportDuration(duration);

      // Backend returns: {report: [...], date_columns: [...], group_columns: [...], all_columns: [...]}
      setReportData(res.data || null);
      setUnlicensedDrivers(res.data.skipped_drivers || []);
      setLoading(false);
      setAbortController(null);
    } catch (err) {
      if (!isMountedRef.current) return;

      if (err.name === "AbortError") {
        setError("Report generation cancelled.");
      } else {
        setError(`Error: ${err.response?.data?.message || err.message}`);
      }
      setLoading(false);
      setAbortController(null);
    }
  };

  const handleSelectAllToggle = useCallback(() => {
    if (selectedDrivers.length === drivers.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(drivers);
    }
  }, [selectedDrivers.length, drivers]);

  const exportToExcel = async () => {
    if (!reportData) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Payroll Report');

      const { report, date_columns, group_columns } = reportData;

      // Build columns dynamically
      const columns = [
        { header: 'No.', key: 'no', width: 6 },
        { header: 'Driver Name', key: 'driver_name', width: 20 }
      ];

      // Add date columns
      date_columns.forEach(date => {
        columns.push({
          header: format(new Date(date + 'T12:00:00'), 'EEE\nMMM dd'),
          key: date,
          width: 15
        });
      });

      // Add group total columns
      group_columns.forEach(group => {
        columns.push({
          header: `Total\n${group}`,
          key: `total_${group}`,
          width: 10
        });
      });

      // Add total_all column
      columns.push({
        header: 'Total\nAll',
        key: 'total_all',
        width: 10
      });

      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      headerRow.height = 30;

      // Add data rows
      report.forEach((entry, index) => {
        const rowData = {
          no: index + 1,
          driver_name: entry.driver_name
        };
        
        // Add date columns data
        date_columns.forEach(date => {
          rowData[date] = entry[date] || '';
        });

        // Add group totals
        group_columns.forEach(group => {
          rowData[`total_${group}`] = entry[`total_${group}`] || 0;
        });

        // Add total_all
        rowData['total_all'] = entry['total_all'] || 0;

        const row = worksheet.addRow(rowData);
        row.alignment = { vertical: 'top', wrapText: true };
        
        // Set row height to accommodate two lines
        row.height = 30;
      });

      // Add borders to all cells
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      // Freeze header row and first two columns
      worksheet.views = [
        { state: 'frozen', xSplit: 2, ySplit: 1 }
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `Payroll_Report_${format(committedStartDate, 'yyyy-MM-dd')}_to_${format(committedEndDate, 'yyyy-MM-dd')}.xlsx`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Excel export error:', err);
      setError(`Failed to export Excel: ${err.message}`);
    }
  };

  const exportToPDF = () => {
    if (!reportData) return;

    try {
      const { report, date_columns, group_columns } = reportData;
      
      const doc = new jsPDF('landscape', 'mm', 'a4');
      
      doc.setFontSize(14);
      doc.text('Payroll Report', 14, 12);
      doc.setFontSize(9);
      doc.text(`Period: ${format(committedStartDate, 'MMM dd, yyyy')} - ${format(committedEndDate, 'MMM dd, yyyy')}`, 14, 18);

      // Build headers dynamically
      const headers = ['No.', 'Driver Name'];
      
      // Add date headers
      date_columns.forEach(d => {
        const dateObj = new Date(d + 'T12:00:00');
        headers.push(`${format(dateObj, 'EEE')}\n${format(dateObj, 'MM/dd')}`);
      });

      // Add group total headers
      group_columns.forEach(group => {
        headers.push(`Total\n${group}`);
      });

      // Add total_all header
      headers.push('Total\nAll');
      
      const tableData = report.map((entry, index) => {
        const row = [index + 1, entry.driver_name];
        
        // Add date columns
        date_columns.forEach(date => {
          const value = entry[date] || '';
          // Replace arrow with simple dash for PDF compatibility
          const cleanValue = value.replace(/→/g, '-').replace(/\n/g, '\n');
          row.push(cleanValue);
        });

        // Add group totals
        group_columns.forEach(group => {
          row.push(entry[`total_${group}`] || 0);
        });

        // Add total_all
        row.push(entry['total_all'] || 0);

        return row;
      });

      // Calculate column widths dynamically
      const numDateCols = date_columns.length;
      const numGroupCols = group_columns.length + 1; // +1 for total_all
      const availableWidth = 277; // A4 landscape width minus margins
      
      const noWidth = 8;
      const nameWidth = 30;
      const totalWidth = 10;
      const remainingWidth = availableWidth - noWidth - nameWidth - (numGroupCols * totalWidth);
      const dateWidth = Math.max(12, remainingWidth / numDateCols);

      const columnStyles = {
        0: { cellWidth: noWidth, halign: 'center' },
        1: { cellWidth: nameWidth, halign: 'left' }
      };

      // Set date column widths
      for (let i = 0; i < numDateCols; i++) {
        columnStyles[i + 2] = { cellWidth: dateWidth, fontSize: 6 };
      }

      // Set total column widths
      for (let i = 0; i < numGroupCols; i++) {
        columnStyles[numDateCols + 2 + i] = { 
          cellWidth: totalWidth, 
          halign: 'center',
          fontStyle: 'bold'
        };
      }

      doc.autoTable({
        startY: 22,
        head: [headers],
        body: tableData,
        theme: 'grid',
        styles: { 
          fontSize: 7, 
          cellPadding: 1,
          minCellHeight: 8,
          valign: 'top'
        },
        headStyles: { 
          fillColor: [68, 114, 196],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          minCellHeight: 10
        },
        columnStyles: columnStyles,
        didParseCell: function(data) {
          // Enable text wrapping for all cells
          if (data.section === 'body' && data.column.index > 1 && data.column.index <= numDateCols + 1) {
            data.cell.styles.cellWidth = dateWidth;
          }
        }
      });

      const fileName = `Payroll_Report_${format(committedStartDate, 'yyyy-MM-dd')}_to_${format(committedEndDate, 'yyyy-MM-dd')}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(`Failed to export PDF: ${err.message}`);
    }
  };

  const reports = reportData?.report || [];
  const dateColumns = reportData?.date_columns || [];
  const groupColumns = reportData?.group_columns || [];

  return (
    <div className="container mt-4">
      <form onSubmit={handleSubmit} className="mb-4">
        <div className="mb-3">
          <label className="form-label" style={{ marginRight: "10px" }}>Select Date Range:</label>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            disabled={loading}
            placeholderText="Select date range"
            maxDate={new Date()}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Select Drivers:</label>
          <div className="d-flex align-items-start gap-2">
            <div style={{ flexGrow: 1 }}>
              <Select
                options={drivers}
                value={selectedDrivers}
                onChange={setSelectedDrivers}
                isMulti
                className="basic-multi-select"
                classNamePrefix="select"
                isDisabled={loading}
                placeholder="Choose drivers..."
                noOptionsMessage={() => "No drivers available"}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm mt-1"
              onClick={handleSelectAllToggle}
              disabled={loading || drivers.length === 0}
            >
              {selectedDrivers.length === drivers.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {loading && (
            <button type="button" className="btn btn-danger" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading report data...</p>
        </div>
      )}

      {reports.length > 0 && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>
              Report Results ({reports.length} drivers)
              {reportDuration && (
                <small className="text-muted ms-2" style={{ fontSize: '0.9rem' }}>
                  - Generated in {reportDuration}s
                </small>
              )}
            </h4>
            <div className="btn-group">
              <button onClick={exportToExcel} className="btn btn-success btn-sm">
                Export to Excel
              </button>
              <button onClick={exportToPDF} className="btn btn-danger btn-sm">
                Export to PDF
              </button>
            </div>
          </div>

          {committedStartDate && committedEndDate && (
            <p className="text-muted">
              Period: {format(committedStartDate, 'MMM dd, yyyy')} - {format(committedEndDate, 'MMM dd, yyyy')}
            </p>
          )}

          {unlicensedDrivers.length > 0 && (
            <div className="alert alert-info mt-3">
              Showing report for licensed drivers only. {unlicensedDrivers.length} driver(s) skipped.
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-bordered table-striped table-hover table-sm">
              <thead className="table-dark">
                <tr>
                  <th style={{ verticalAlign: 'middle' }}>No.</th>
                  <th style={{ verticalAlign: 'middle' }}>Driver Name</th>
                  {dateColumns.map(date => (
                    <th key={date} className="text-center" style={{ minWidth: '100px' }}>
                      {format(new Date(date + 'T12:00:00'), 'EEE')}<br/>
                      <small>{format(new Date(date + 'T12:00:00'), 'MMM dd')}</small>
                    </th>
                  ))}
                  {groupColumns.map(group => (
                    <th key={group} className="text-center table-info" style={{ minWidth: '80px' }}>
                      Total<br/>
                      <small>{group}</small>
                    </th>
                  ))}
                  <th className="text-center table-warning" style={{ minWidth: '70px' }}>
                    Total<br/>
                    <small>All</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((entry, index) => (
                  <tr key={index}>
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>{index + 1}</td>
                    <td style={{ verticalAlign: 'middle' }}>{entry.driver_name}</td>
                    {dateColumns.map(date => (
                      <td key={date} className="text-center" style={{ fontSize: '0.85rem', whiteSpace: 'pre-line', verticalAlign: 'top', padding: '8px 4px' }}>
                        {entry[date] ? entry[date] : '-'}
                      </td>
                    ))}
                    {groupColumns.map(group => (
                      <td key={group} className="text-center table-info" style={{ verticalAlign: 'middle', fontWeight: 'bold' }}>
                        {entry[`total_${group}`] || 0}
                      </td>
                    ))}
                    <td className="text-center table-warning" style={{ verticalAlign: 'middle', fontWeight: 'bold' }}>
                      {entry['total_all'] || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {unlicensedDrivers.length > 0 && (
              <div className="alert alert-warning mt-4">
                <h5>⚠️ Unlicensed Drivers</h5>
                <p className="mb-1">These drivers were skipped due to insufficient payroll licenses:</p>
                <ul className="mb-0">
                  {unlicensedDrivers.map((id) => {
                    const driver = drivers.find((d) => d.value === id);
                    return (
                      <li key={id}>
                        {driver ? driver.label : `Driver ID: ${id}`}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && reports.length === 0 && committedStartDate && (
        <div className="alert alert-info">
          No data found for the selected drivers and date range.
        </div>
      )}
    </div>
  );
}

export default PayrollReportPage;